import './WithholdingOptions.scss';

import React, { Fragment } from 'react';
import { useState, useEffect, useRef } from 'react';

import WithholdingDisclosureModal from './WithholdingDisclosureModal';
import { marginalRateTableUrl, footerLinks } from 'services/Externals';
import classnames from 'classnames';
import Time from 'services/Time';
import {
  AAMBeaconControl,
  AAMBeaconControlGroup
} from "components/shared/Beacon";

const WithholdingOptions = (
    {
        agreementChecked,
        isWithholding,
        initialWithholdingValue,
        withholdingValue,
        accountTypeId,
        handleOptionChange,
        handleAmountChange,
        handleAgreementChecked
    }
) => {
    const stepperListener = useRef(null);
    const [showModal, setShowModal] = useState(false);
    const [isWithHoldingInvalid, setIsWithHoldingInvalid] = useState(false);

    useEffect(() => {
      stepperListener.current.addEventListener('change', handleWithholdingValueChange);
      return () => {
        stepperListener.current.removeEventListener('change', handleWithholdingValueChange);
      };
    }, [])

    useEffect(() => {
        if(isWithholding) {
            if (withholdingValue < 0 || withholdingValue > 99) {
                setIsWithHoldingInvalid(true);
            } else {
                setIsWithHoldingInvalid(false);
            }
        }
    }, [isWithholding, withholdingValue]);

    // Checks if withholding percent has been modified
    const handleWithholdingValueChange = (event) => {
      if(event.detail) {
        const withholdingValue = parseInt(event.detail.value) || 0;
        if (withholdingValue < 0 || withholdingValue > 99) {
          setIsWithHoldingInvalid(true);
        } else {
          setIsWithHoldingInvalid(false);
        }
        handleAmountChange(withholdingValue);
      }
    };

    // Checks if agreement box has been checked
    const onAgreementChange = (agreementChecked) => {
        handleAgreementChecked(agreementChecked);
    };

    const withHoldingLabelClassname = classnames('withholding-value-label-v2', {
        'withholding-error-v2': isWithholding && isWithHoldingInvalid
    });

    return (
        <div className="withholding-option-v2 beacon-on-light">
            {showModal && (
                <WithholdingDisclosureModal
                    onClose={() => setShowModal(false)}
                    accountTypeId={accountTypeId}
                />
            )}
            {/*<div className="is-withholding-label-v2">Cash Dividend Withholding</div>*/}
            <div className="is-withholding-options-v2">
                {/* Withholding election */}
              <AAMBeaconControlGroup>
                <div slot="label">Cash Dividend Withholding</div>
                <AAMBeaconControl inline>
                  <label htmlFor="withholding-yes"
                         slot="label">
                    Yes
                  </label>
                  <input type="radio"
                         value={true}
                         id="withholding-yes"
                         checked={isWithholding || withholdingValue === null}
                         onChange={(event) => {
                           handleOptionChange(event.target.value === "true");
                           onAgreementChange(false);
                         }} />
                </AAMBeaconControl>
                <AAMBeaconControl inline>
                  <label htmlFor="withholding-no"
                         slot="label">
                    No
                  </label>
                  <input type="radio"
                         value={false}
                         id="withholding-no"
                         checked={!isWithholding}
                         onChange={(event) => {
                           handleOptionChange(event.target.value === "true");
                           onAgreementChange(false);
                         }} />
                </AAMBeaconControl>
              </AAMBeaconControlGroup>
            </div>
            <Fragment>
                <div className={withHoldingLabelClassname}>Withholding Percentage</div>
                <div className="withholding-wrapper">
                    <div className="withholding-input-value-disclosure">
                        {/* Withholding percentage */}
                        <div className="withholding-value-input-v2">
                          <AAMBeaconControl stepper-variant="primary"
                                            ref={stepperListener}>
                            <input type="number"
                                   id={'withholding-value-input'}
                                   name="Withholding Percentage"
                                   disabled={!isWithholding}
                                   value={withholdingValue}
                                   required={true}
                                   min="0"
                                   max="99"
                            />
                            <div slot="error">Invalid percentage</div>
                          </AAMBeaconControl>
                        </div>
                        <div
                            className="withholding-disclosure-wrapper-v2"
                            data-testid="cashWithholdingDisclosure"
                        >
                            Edit this field if you would like a rate of withholding that is
                            different from the default withholding rate. See the
                            instructions on page 2 of the IRS Form W-4R and the Marginal
                            Rate Tables for additional information. Enter the rate as a
                            whole number (no decimals). The following link will take you to
                            the{' '}
                            <a
                                className="income-tax-withholding-disclosure-anchor"
                                target="_blank"
                                rel={'noreferrer'}
                                href={marginalRateTableUrl}
                                data-testid={'marginal-rate-tables-link-test'}>
                                {Time.getCurrentYear()} Marginal Rate Tables.
                            </a>{' '}
                            You may use these tables to help you select the appropriate
                            withholding rate for this payment or distribution. Instructions
                            on how to best use them are included.
                        </div>
                    </div>
                    {(!isWithholding || (isWithholding && !isWithHoldingInvalid && initialWithholdingValue !== withholdingValue)) && (
                        <div className="withholding-disclosure-agreement-v2">
                            <div className={"withholding-disclosure-notice-v2"}>
                                <div className={"withholding-disclosure-title-v2"}>
                                    Federal and State Income Tax Withholding Notice
                                </div>
                                <div className={"withholding-disclosure-text-v2"}>
                                    Your withholding rate is determined by the type of payment you will
                                    receive. For nonperiodic payments, the default withholding rate is
                                    10% and you can select to have a different rate by entering a rate
                                    between 0% and 99% on the following step. You can&apos;t choose
                                    less than 10% for payments to be delivered outside the United
                                    States and its territories. Please{' '}
                                    <a
                                        href={footerLinks.contactUsUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        contact us
                                    </a>{' '}
                                    if you want an election of 100%.
                                    <br></br><br></br>
                                    State income tax will be withheld from your distribution under the
                                    applicable state law if your address of record is in a state that
                                    defaults to withholding.
                                    <br></br><br></br>
                                    <b>Caution:</b> If you have too little tax withheld, you will generally
                                    owe tax when you file your tax return and may owe a penalty unless
                                    you make timely payments of estimated tax. If too much tax is
                                    withheld, you will generally be due a refund when you file your tax
                                    return. Your withholding choice (or an election not to have
                                    withholding on a nonperiodic payment) will generally apply to any
                                    future payment from the same plan or IRA. Submit a new Form W-4R if
                                    you want to change your election.
                                </div>
                            </div>
                            {/* Withholding disclosure checkbox */}
                            <div className={"withholding-disclosure-agreement-check-box-v2"}>
                              <AAMBeaconControl inline>
                                <label slot="label"
                                       htmlFor="tax-withholding-disclosure">
                                  I have read the Income Tax Withholding Disclosure
                                </label>
                                <input type="checkbox"
                                       id='tax-withholding-disclosure'
                                       checked={agreementChecked}
                                       onChange={() => {
                                         onAgreementChange(!agreementChecked);
                                       }}
                                       required />
                              </AAMBeaconControl>
                            </div>
                        </div>
                    )}
                </div>
            </Fragment>
        </div>
    );
}
export default WithholdingOptions;
